/**
 * CORE-1749 — ответ на фразу из сообщения Агапеллы.
 *
 * Человек выделяет кусок ответа («Дыхание 4-7-8» из трёх практик) и спрашивает
 * про него. Фраза уходит рядом с `replyToMessageId` (`SendMessageV2`,
 * `replyToFragment`), и сервер принимает её, только если она есть в цитируемом
 * (`ReplyFragment` в Agape.ChatApi) — иначе ИИ получил бы «ты говорила» про
 * слова, которых она не говорила.
 *
 * Выделяют на экране, а не в исходном тексте: разметку там уже нарисовали
 * (`renderTextWithLinks`) — жирное без звёздочек, ссылка — заголовком, пункт
 * списка — точкой, эмодзи комплекта — картинкой. Поэтому сравнение здесь и на
 * сервере — в одном виде (`canonicalQuoteText`): разметка снята, пробелы
 * схлопнуты, слова и их порядок — как были. Правка правил — в обоих местах.
 *
 * Без React — проверяется в node.
 */

/** Потолок фразы — тот же, что у сервера (`ReplyFragment.MaxLength`). */
export const REPLY_FRAGMENT_MAX = 500;

// \u0421\u043A\u043B\u0435\u0439\u043A\u0430 \u043D\u0443\u043B\u0435\u0432\u043E\u0439 \u0448\u0438\u0440\u0438\u043D\u044B (U+200D) \u2014 \u043E\u0442\u0434\u0435\u043B\u044C\u043D\u043E\u0439 \u0432\u0435\u0442\u043A\u043E\u0439: \u0432\u043D\u0443\u0442\u0440\u0438 \u043A\u043B\u0430\u0441\u0441\u0430 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432
// \u043B\u0438\u043D\u0442\u0435\u0440 \u0447\u0438\u0442\u0430\u0435\u0442 \u0435\u0451 \u043A\u0430\u043A \u00AB\u0441\u043E\u0435\u0434\u0438\u043D\u0451\u043D\u043D\u0443\u044E \u043F\u043E\u0441\u043B\u0435\u0434\u043E\u0432\u0430\u0442\u0435\u043B\u044C\u043D\u043E\u0441\u0442\u044C\u00BB.
const SPACES = /(?:[\s\u00A0\u202F\u2007\u200B\u200C\uFEFF]|\u200D)+/g;

/**
 * Фраза в том виде, в каком уходит на сервер: пробелы схлопнуты, края
 * обрезаны, не длиннее {@link REPLY_FRAGMENT_MAX}. Длинное выделение режется по
 * началу — начало выделенного куска остаётся куском того же сообщения, и
 * сервер его примет. `null` — выделять нечего.
 */
export function cleanReplyFragment(text: string | null | undefined): string | null {
  const cleaned = (text ?? '').replace(SPACES, ' ').trim();
  if (!cleaned) return null;
  if (cleaned.length <= REPLY_FRAGMENT_MAX) return cleaned;
  // Не резать эмодзи пополам: половина суррогатной пары — битая строка.
  let cut = REPLY_FRAGMENT_MAX;
  const code = cleaned.charCodeAt(cut - 1);
  if (code >= 0xd800 && code <= 0xdbff) cut -= 1;
  return cleaned.slice(0, cut).trim();
}

/** Текст в виде для сравнения — построчно так же, как `ReplyFragment.Canonical`. */
export function canonicalQuoteText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .normalize('NFC')
    .replace(/@\[([^\]\n]+)\]\(user:[^)\s]+\)/g, '@$1')
    .replace(/\[([^\]\n]+?)\]\((https?:\/\/[^\s)]+)\)/g, '$1')
    .replace(/:agape_\d+:/g, ' ')
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-+*•]\s+/gm, '')
    .replace(/^\s*(\d+)[.)]\s+/gm, '$1. ')
    .replace(/[*`~▎•]/g, '')
    .replace(SPACES, ' ')
    .trim();
}

/** Фраза действительно есть в сообщении — та же проверка, что сделает сервер. */
export function isFragmentOf(fragment: string | null | undefined, original: string | null | undefined): boolean {
  const needle = canonicalQuoteText(cleanReplyFragment(fragment));
  if (!needle) return false;
  return canonicalQuoteText(original).includes(needle);
}

/**
 * Текст сообщения так, как его видит человек, — для листа «Спросить о фразе» в
 * приложении. Там нет события выделения у обычного `Text`, и выделяют в поле
 * `TextInput`, которое разметку не рисует: без этого человек выделял бы
 * звёздочки и квадратные скобки. Всё, что здесь снято, снимает и сервер, так
 * что выделенное в этом тексте проходит его проверку.
 */
export function plainMessageText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/```[^\n]*\n?([\s\S]*?)```/g, '$1')
    .replace(/@\[([^\]\n]+)\]\(user:[^)\s]+\)/g, '@$1')
    .replace(/\[([^\]\n]+?)\]\((https?:\/\/[^\s)]+)\)/g, '$1')
    .replace(/[ \t]*:agape_\d+:/g, '')
    .replace(/^(\s*)#{1,6}\s+/gm, '$1')
    .replace(/^(\s*)>\s?/gm, '$1')
    .replace(/^(\s*)[-+*]\s+/gm, '$1• ')
    .replace(/\*\*\*|\*\*|~~|`/g, '')
    .replace(/\*([^*\n]+?)\*/g, '$1')
    .trim();
}

/**
 * Выделенная часть текста — или ничего (лист «Спросить о фразе», `TextInput`
 * отдаёт `{ start, end }`). Пустое и пробельное выделение кнопку не включает;
 * длинное режется по началу (`cleanReplyFragment`).
 */
export function selectedFragment(text: string, selection: { start: number; end: number }): string | null {
  const start = Math.max(0, Math.min(selection.start, selection.end));
  const end = Math.min(text.length, Math.max(selection.start, selection.end));
  if (end <= start) return null;
  return cleanReplyFragment(text.slice(start, end));
}

/**
 * Строка цитаты над ответом и над полем ввода. Фраза — в кавычках, как сказано;
 * без фразы — начало сообщения. У сообщения ИИ разметка снята: «**Дыхание**» в
 * две строки цитаты читалось бы со звёздочками.
 */
export function quoteLine(
  preview: { text: string; fragment?: string | null; senderId?: string },
  fallback: (text: string) => string,
): string {
  if (preview.fragment) return `«${preview.fragment}»`;
  const fromAi = !!preview.senderId && preview.senderId.startsWith('ai_');
  return fallback(fromAi ? plainMessageText(preview.text) : preview.text);
}
